import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock3, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { reportError } from "../lib/reportError";
import { useLanguage } from "../context/LanguageContext";
import { BankPaymentDetails } from "../components/payment/BankPaymentDetails";

export default function BankPayment() {
  const { orderId } = useParams();
  const { t } = useLanguage();
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async signal => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/orders/${orderId}/bank-payment`, { signal });
      if (!signal?.aborted) setPayment(data);
    } catch (error) {
      if (signal?.aborted) return;
      reportError("Bank payment could not be loaded", error);
      setError("Payment details could not be loaded. Please check the order link or try again.");
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [orderId]);
  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);
  const verified = payment?.status === "verified";
  return <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
    <p className="eyebrow">{t("Swiss QR payment")}</p>
    <h1 data-testid="bank-payment-heading" className="mt-3 font-display text-3xl font-bold text-slate-50 sm:text-4xl">{t(verified ? "Payment verified" : "Your QR payment slip")}</h1>
    <p data-testid="bank-order-reference" className="mt-3 break-all font-mono text-xs text-slate-500">{t("Order")} · {orderId.toUpperCase()}</p>
    {error && <p data-testid="bank-payment-error" role="alert" className="mt-6 text-sm text-rose-300">{t(error)}</p>}
    {payment && <>
      <div data-testid="bank-payment-status" role="status" className="mt-6 flex items-start gap-3 border-l-2 border-[#E5A93C] bg-[#141620] p-4">
        {verified ? <CheckCircle2 className="shrink-0 text-emerald-400" size={20} /> : <Clock3 className="shrink-0 text-[#E5A93C]" size={20} />}
        <div><p className="text-sm font-semibold text-slate-100">{t(verified ? "Status: In Production" : "Awaiting bank-payment verification")}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-400">{t(verified ? "The studio has confirmed your bank transfer. Please do not pay again." : "Production begins only after the studio confirms receipt of your payment. Bank processing times vary; same-day credit is not guaranteed.")}</p></div>
      </div>
      {!verified && <BankPaymentDetails payment={payment} />}
    </>}
    <div className="mt-8 flex flex-wrap gap-3">
      <button data-testid="bank-refresh-status" onClick={() => load()} disabled={loading} className="btn-ghost"><RefreshCw size={16} className={loading ? "animate-spin" : ""} />{t(loading ? "Loading…" : "Check payment status")}</button>
      <Link data-testid="bank-home-link" to="/" className="btn-ghost">{t("Back to Home")}</Link>
    </div>
  </div>;
}